export interface PeriodComparison {
  current: number;
  previous: number;
  change: number;
  changePercent: number;
  isPositive: boolean;
}

export interface TrendData {
  date: string;
  value: number;
  label?: string;
}

/**
 * Calculate period-over-period comparison
 */
export function calculatePeriodComparison(
  currentValue: number,
  previousValue: number
): PeriodComparison {
  const change = currentValue - previousValue;
  const changePercent = previousValue !== 0 ? (change / previousValue) * 100 : 0;
  
  return {
    current: currentValue,
    previous: previousValue,
    change,
    changePercent: Math.abs(changePercent),
    isPositive: change >= 0,
  };
}

/**
 * Calculate growth rate
 */
export function calculateGrowthRate(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

/**
 * Calculate inventory turnover ratio
 * Formula: Cost of Goods Sold / Average Inventory
 */
export function calculateInventoryTurnover(
  costOfGoodsSold: number,
  averageInventory: number
): number {
  if (averageInventory === 0) return 0;
  return costOfGoodsSold / averageInventory;
}

/**
 * Calculate stock accuracy percentage
 * Formula: (Physical Count / System Count) * 100
 */
export function calculateStockAccuracy(
  physicalCount: number,
  systemCount: number
): number {
  if (systemCount === 0) return 100;
  return Math.min((physicalCount / systemCount) * 100, 100);
}

/**
 * Calculate fulfillment rate
 * Formula: (Fulfilled Orders / Total Orders) * 100
 */
export function calculateFulfillmentRate(
  fulfilledOrders: number,
  totalOrders: number
): number {
  if (totalOrders === 0) return 0;
  return (fulfilledOrders / totalOrders) * 100;
}

/**
 * Calculate average order value
 */
export function calculateAverageOrderValue(
  totalRevenue: number,
  totalOrders: number
): number {
  if (totalOrders === 0) return 0;
  return totalRevenue / totalOrders;
}

/**
 * Get date range for a time period
 */
export function getDateRangeForPeriod(period: {
  value: string;
  days: number;
  customStart?: string;
  customEnd?: string;
}): { start: Date; end: Date } {
  if (period.customStart && period.customEnd) {
    const start = new Date(period.customStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(period.customEnd);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  const end = new Date();
  end.setHours(23, 59, 59, 999);
  
  const start = new Date();
  
  switch (period.value) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case 'week':
      start.setDate(start.getDate() - 7);
      break;
    case 'month':
      start.setMonth(start.getMonth() - 1);
      break;
    case 'quarter':
      start.setMonth(start.getMonth() - 3);
      break;
    case 'year':
      start.setFullYear(start.getFullYear() - 1);
      break;
    default:
      start.setDate(start.getDate() - period.days);
  }
  
  start.setHours(0, 0, 0, 0);
  
  return { start, end };
}

/**
 * Get previous period date range
 */
export function getPreviousPeriodRange(period: {
  value: string;
  days: number;
}): { start: Date; end: Date } {
  const { start: currentStart, end: currentEnd } = getDateRangeForPeriod(period);
  const duration = currentEnd.getTime() - currentStart.getTime();
  
  const end = new Date(currentStart);
  end.setTime(end.getTime() - 1);
  end.setHours(23, 59, 59, 999);
  
  const start = new Date(end);
  start.setTime(start.getTime() - duration);
  start.setHours(0, 0, 0, 0);
  
  return { start, end };
}

/**
 * Format trend data for charts
 */
export function formatTrendData(
  data: Array<{ date: Date | string; value: number }>,
  groupBy: 'day' | 'week' | 'month' = 'day'
): TrendData[] {
  if (!data || data.length === 0) return [];
  
  const grouped = new Map<string, { sum: number; count: number }>();
  
  data.forEach((item) => {
    const date = typeof item.date === 'string' ? new Date(item.date) : item.date;
    let key: string;
    
    switch (groupBy) {
      case 'week':
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toISOString().split('T')[0];
        break;
      case 'month':
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        break;
      default:
        key = date.toISOString().split('T')[0];
    }
    
    const existing = grouped.get(key) || { sum: 0, count: 0 };
    grouped.set(key, {
      sum: existing.sum + item.value,
      count: existing.count + 1,
    });
  });
  
  return Array.from(grouped.entries())
    .map(([date, { sum }]) => ({
      date,
      value: sum,
      label: date,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Calculate top items by value
 */
export function getTopItemsByValue<T extends { value: number }>(
  items: T[],
  limit: number = 10
): T[] {
  return [...items]
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
}

/**
 * Calculate category distribution
 */
export function calculateCategoryDistribution(
  items: Array<{ category?: string; value: number }>
): Array<{ label: string; value: number; percentage: number }> {
  const categoryMap = new Map<string, number>();
  let total = 0;
  
  items.forEach((item) => {
    const category = item.category || 'Uncategorized';
    const current = categoryMap.get(category) || 0;
    categoryMap.set(category, current + item.value);
    total += item.value;
  });
  
  return Array.from(categoryMap.entries())
    .map(([label, value]) => ({
      label,
      value,
      percentage: total > 0 ? (value / total) * 100 : 0,
    }))
    .sort((a, b) => b.value - a.value);
}
